import type {
  CreateTagRequest,
  ListResponse,
  TagDeleteResult,
  TagEntityKind,
  TagQuery,
  TagResponse,
  UpdateTagRequest,
} from '@troia-v3/types';

import { api, type ApiResponse } from '@/lib/api';

const BASE_URL = '/tags';

/** O que `PUT /tags/:id` devolve além da tag: quantos documentos a cascata tocou. */
export interface TagUpdateResult {
  tag: TagResponse;
  updated: Record<TagEntityKind, number>;
}

/**
 * Cliente da API do catálogo de tags.
 *
 * - GET    /api/tags?withUsage=true — listagem da tela de gestão (com uso)
 * - POST   /api/tags                — criar
 * - PUT    /api/tags/:id            — renomear (com cascata), cor, descrição, status
 * - DELETE /api/tags/:id[?purge=]   — excluir do catálogo (e opcionalmente das entidades)
 *
 * `GET /tags/dropdown` é consumido por `@/shared/hooks/useTagCatalog`
 * (cross-module, sem permissão de módulo) — não duplicado aqui.
 */
export const tagsApi = {
  async list(query?: TagQuery): Promise<ListResponse<TagResponse>> {
    const response = await api.get<ApiResponse<ListResponse<TagResponse>>>(BASE_URL, { params: query });
    return response.data.data!;
  },

  async create(data: CreateTagRequest): Promise<TagResponse> {
    const response = await api.post<ApiResponse<TagResponse>>(BASE_URL, data);
    return response.data.data!;
  },

  /**
   * O `meta.updated` da resposta é o que permite a tela dizer "renomeada em 38
   * contatos" em vez de um "salvo" que não conta o que aconteceu com os outros
   * 38 registros.
   */
  async update(id: string, data: UpdateTagRequest): Promise<TagUpdateResult> {
    const response = await api.put<ApiResponse<TagResponse> & { meta?: { updated: Record<TagEntityKind, number> } }>(
      `${BASE_URL}/${id}`,
      data,
    );
    return {
      tag: response.data.data!,
      updated: response.data.meta?.updated ?? { contact: 0, ticket: 0, customer: 0, conversation: 0 },
    };
  },

  /**
   * `purge: false` (padrão) tira a tag só do catálogo — a palavra continua nos
   * contatos, tickets e clientes que já estavam marcados. É o padrão porque a
   * marcação é histórico de quem atendeu, e apagá-la não tem volta.
   */
  async remove(id: string, purge = false): Promise<TagDeleteResult> {
    const response = await api.delete<ApiResponse<TagDeleteResult>>(`${BASE_URL}/${id}`, {
      params: purge ? { purge: true } : undefined,
    });
    return response.data.data!;
  },
};
